import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  Container,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  Navbar,
  NavbarBrand,
  UncontrolledCollapse,
} from 'reactstrap';
import { BUNDLES, getBundlePrice, SUBSCRIPTION_PLANS } from 'config/subscriptionPlans';
import { getSpecialty } from 'config/specialtyConfig';

// Custom CSS import
import 'assets/css/pmr-landing.css';

const PMRPrepHome = () => {
  const [isVisible, setIsVisible] = useState({});
  const [scrollY, setScrollY] = useState(0);
  const [navbarScrolled, setNavbarScrolled] = useState(false);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({
              ...prev,
              [entry.target.id]: true,
            }));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // Parallax scroll effect and navbar styling
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
      setNavbarScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const features = [
    {
      icon: 'fas fa-brain',
      title: 'Comprehensive Question Bank',
      description: 'Over 2,000+ board-style questions covering all PM&R topics with detailed explanations.',
      color: '#2dce89',
    },
    {
      icon: 'fas fa-chart-line',
      title: 'Performance Analytics',
      description: 'Track your progress with detailed analytics and identify areas for improvement.',
      color: '#5e72e4',
    },
    {
      icon: 'fas fa-clock',
      title: 'Timed Practice Exams',
      description: 'Simulate real exam conditions with customizable timed practice tests.',
      color: '#fb6340',
    },
    {
      icon: 'fas fa-mobile-alt',
      title: 'Study Anywhere',
      description: 'Access your question bank on any device - desktop, tablet, or mobile.',
      color: '#11cdef',
    },
    {
      icon: 'fas fa-robot',
      title: 'AI Tutor',
      description: 'Get personalized explanations and guidance powered by AI to help you master each topic.',
      color: '#f5365c',
    },
    {
      icon: 'fas fa-users',
      title: 'Expert Authored',
      description: 'Questions written and reviewed by PM&R physicians.',
      color: '#ffd600',
    },
  ];

  const stats = [
    { number: '2,000+', label: 'Practice Questions' },
    { number: '100+', label: 'Topics Covered' },
    { number: '10+', label: 'Subject Areas' },
    { number: '24/7', label: 'Access Anywhere' },
  ];

  const testimonials = [
    {
      name: 'Dr. Bagheri',
      role: 'PM&R Resident',
      text: 'This PMR question bank is top-notch! The questions are incredibly similar to the SAE, making it one of the most reliable resources out there. Explanations are clear, concise, and super helpful for mastering tricky concepts. After using this, I felt fully prepared and confident going into the exam. I highly recommend this question bank!',
      avatar: 'KB',
    },
    {
      name: 'Dr. Yasin',
      role: 'PGY-4 Resident',
      text: 'This question bank is a gold mine for studying for the PM&R boards. I love how the questions are broken down to match the exact guide that the ABPMR gives. The answer explanations are also very thorough, so it helps with reviewing information. And I love using the AI tutor to study more in-depth whenever I get a question wrong.',
      avatar: 'DY',
    },
    {
      name: 'Dr. Martinez',
      role: 'Pain Fellow',
      text: 'I was an early beta tester for this question bank, and unfortunately, only got to use it towards the end of my boards prep. I wish I had this from the very beginning of my study as it would\'ve made my life a lot easier. The question bank is extremely complete. I love that they have over 2,000 questions and the fact that they\'re also going to have full-length exams is such a great tool for evaluating being ready for the real thing.',
      avatar: 'DM',
    },
  ];

  const topics = [
    'Musculoskeletal Medicine',
    'Neuromuscular Medicine',
    'Pediatric Rehabilitation',
    'Brain Injury Medicine',
    'Spinal Cord Medicine',
    'Pain Medicine',
    'Electrodiagnostic Medicine',
    'Sports Medicine',
    'Prosthetics & Orthotics',
    'Cardiac Rehabilitation',
    'Pulmonary Rehabilitation',
    'Cancer Rehabilitation',
  ];

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>PM&R Prep | #1 Physical Medicine & Rehabilitation Board Review Question Bank</title>
        <meta
          name="description"
          content="Pass your PM&R boards with confidence. 2,000+ board-style questions, detailed explanations, and performance analytics. Built by PM&R physicians."
        />
        <meta
          name="keywords"
          content="PM&R board review, physiatry board prep, PM&R question bank, physical medicine rehabilitation exam, ABPMR board review, PM&R practice questions, physiatry exam prep, rehabilitation medicine boards"
        />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="PM&R Prep" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://pmrprep.io/" />
        <meta property="og:title" content="PM&R Prep | #1 Physical Medicine & Rehabilitation Board Review" />
        <meta
          property="og:description"
          content="Pass your PM&R boards with confidence. 2,000+ board-style questions with detailed explanations."
        />
        <meta property="og:image" content="https://pmrprep.io/pmr-og-image.png" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://pmrprep.io/" />
        <meta name="twitter:title" content="PM&R Prep | Board Review Question Bank" />
        <meta
          name="twitter:description"
          content="Pass your PM&R boards with confidence. 2,000+ practice questions."
        />
        <meta name="twitter:image" content="https://pmrprep.io/pmr-og-image.png" />

        {/* Canonical URL */}
        <link rel="canonical" href="https://pmrprep.io/" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Course',
            name: 'PM&R Prep - Board Review Question Bank',
            description: 'The most comprehensive Physical Medicine & Rehabilitation board review question bank. 2,000+ board-style questions with detailed explanations covering 100+ topics.',
            url: 'https://pmrprep.io',
            provider: {
              '@type': 'Organization',
              name: 'PM&R Prep',
              url: 'https://pmrprep.io',
              logo: 'https://pmrprep.io/pmr-logo.png',
            },
            offers: {
              '@type': 'Offer',
              price: '29',
              priceCurrency: 'USD',
              availability: 'https://schema.org/InStock',
              url: 'https://pmrprep.io/auth/register',
            },
          })}
        </script>
      </Helmet>

      <div className="pmr-landing">
        {/* Navigation */}
        <Navbar className={`pmr-navbar navbar-horizontal navbar-dark fixed-top ${navbarScrolled ? 'scrolled' : ''}`} expand="lg">
          <Container>
            <NavbarBrand to="/" tag={Link} className="pmr-brand">
              <img
                alt="PM&R Prep Logo"
                src={require('assets/img/brand/pmr-logo.png')}
                className="pmr-nav-logo"
              />
            </NavbarBrand>
            <button className="navbar-toggler" id="pmr-navbar-collapse">
              <span className="navbar-toggler-icon" />
            </button>
            <UncontrolledCollapse navbar toggler="#pmr-navbar-collapse">
              <Nav className="ml-auto align-items-center" navbar>
                <NavItem>
                  <NavLink href="#features" className="pmr-nav-link">
                    Features
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink href="#fellowship" className="pmr-nav-link">
                    Fellowship
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink href="#testimonials" className="pmr-nav-link">
                    Testimonials
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink href="#pricing" className="pmr-nav-link">
                    Pricing
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink href="#bundles" className="pmr-nav-link">
                    Bundles
                  </NavLink>
                </NavItem>
                <NavItem className="ml-lg-3">
                  <Link
                    to="/free-anki-deck"
                    className="btn btn-sm pmr-btn-primary"
                  >
                    <i className="fas fa-download mr-1" />
                    Free Anki Deck
                  </Link>
                </NavItem>
                <NavItem className="ml-lg-2">
                  <Link
                    to="/auth/login"
                    className="btn btn-sm pmr-btn-outline"
                  >
                    Login
                  </Link>
                </NavItem>
                <NavItem className="ml-lg-2">
                  <Link
                    className="btn btn-sm pmr-btn-primary"
                    to="/auth/register"
                  >
                    Start Free Trial
                  </Link>
                </NavItem>
              </Nav>
            </UncontrolledCollapse>
          </Container>
        </Navbar>

        {/* Hero Section */}
        <section className="pmr-hero">
          <div
            className="pmr-hero-bg"
            style={{ transform: `translateY(${scrollY * 0.5}px)` }}
          />
          <div className="pmr-hero-overlay" />
          <Container className="pmr-hero-content">
            <Row className="align-items-center min-vh-100">
              <Col lg="6" className="text-center text-lg-left">
                <div className="pmr-hero-text animate-fade-in-up">
                  <span className="pmr-badge">
                    <i className="fas fa-star mr-2" />
                    #1 PM&R Board Review Platform
                  </span>
                  <h1 className="pmr-hero-title">
                    Pass Your <span className="pmr-text-gradient">PM&R Boards</span> With Confidence
                  </h1>
                  <p className="pmr-hero-subtitle">
                    Master Physical Medicine & Rehabilitation with our comprehensive question bank.
                    2,000+ board-style questions with detailed explanations written by experts.
                  </p>
                  <div className="pmr-hero-cta">
                    <Link
                      className="btn pmr-btn-primary pmr-btn-lg mr-3"
                      to="/auth/register"
                    >
                      Start Free Trial
                      <i className="fas fa-arrow-right ml-2" />
                    </Link>
                    <a
                      className="btn pmr-btn-secondary pmr-btn-lg"
                      href="#features"
                    >
                      Learn More
                    </a>
                  </div>
                  <div className="pmr-hero-trust mt-4">
                    <span className="pmr-trust-text">
                      <i className="fas fa-shield-alt mr-2" />
                      Built by PM&R physicians
                    </span>
                  </div>
                </div>
              </Col>
              <Col lg="6" className="d-none d-lg-block">
                <div className="pmr-hero-image animate-fade-in-right">
                  <div className="pmr-floating-card pmr-card-1 animate-float">
                    <i className="fas fa-check-circle text-success mr-2" />
                    <span>Question Completed!</span>
                  </div>
                  <div className="pmr-floating-card pmr-card-2 animate-float-delayed">
                    <i className="fas fa-chart-line text-info mr-2" />
                    <span>Score: 92%</span>
                  </div>
                  <img
                    src={require('assets/img/brand/pmr-logo.png')}
                    alt="PM&R Prep Dashboard"
                    className="pmr-hero-logo-main"
                  />
                </div>
              </Col>
            </Row>
          </Container>
          <div className="pmr-hero-wave">
            <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z"
                fill="#ffffff"
              />
            </svg>
          </div>
        </section>

        {/* Free Anki Deck Lead Magnet */}
        <section className="pmr-group-pricing" id="free-anki-deck">
          <Container>
            <Row className="align-items-center">
              <Col lg="6" className="mb-5 mb-lg-0">
                <div className="animate-on-scroll opacity-0 animate-fade-in-up">
                  <span className="pmr-group-badge">FREE DOWNLOAD</span>
                  <h2 className="pmr-section-title" style={{ marginTop: '1rem' }}>
                    Get Our PM&R{' '}
                    <span className="pmr-text-gradient">Anki Deck Free</span>
                  </h2>
                  <p style={{ fontSize: '1.125rem', color: '#8898aa', lineHeight: 1.7, marginBottom: '2rem' }}>
                    High-yield flashcards built straight from the PM&R Review Guide.
                    No trial required — just tell us where to send it.
                  </p>
                  <div className="pmr-group-benefits">
                    <div className="pmr-group-benefit">
                      <i className="fas fa-check-circle" />
                      <span>Built for the ABPMR board exam</span>
                    </div>
                    <div className="pmr-group-benefit">
                      <i className="fas fa-check-circle" />
                      <span>Works in the free Anki app</span>
                    </div>
                    <div className="pmr-group-benefit">
                      <i className="fas fa-check-circle" />
                      <span>Every future update included, free</span>
                    </div>
                  </div>
                  <Link
                    to="/free-anki-deck"
                    className="btn pmr-btn-primary pmr-btn-lg"
                    style={{ marginTop: '2rem' }}
                  >
                    Claim the Free Deck <i className="fas fa-arrow-right ml-2" />
                  </Link>
                </div>
              </Col>
              <Col lg="6">
                <div className="animate-on-scroll opacity-0 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                  <div className="pmr-group-card">
                    <div className="pmr-group-card-icon">
                      <i className="fas fa-layer-group" />
                    </div>
                    <h3 className="pmr-group-card-title">PM&R Anki Deck</h3>
                    <p className="pmr-group-card-subtitle">Spaced repetition, done right</p>
                    <div className="pmr-group-card-highlight">
                      <i className="fas fa-gift mr-2" />
                      Completely Free
                    </div>
                    <ul className="pmr-group-card-features">
                      <li><i className="fas fa-check mr-2" />Mapped to the ABPMR content outline</li>
                      <li><i className="fas fa-check mr-2" />Commit facts to long-term memory</li>
                      <li><i className="fas fa-check mr-2" />Study on desktop or mobile</li>
                      <li><i className="fas fa-check mr-2" />Updated as the deck grows</li>
                    </ul>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Group Pricing Section */}
        <section className="pmr-group-pricing" id="group-pricing">
          <Container>
            <Row className="align-items-center">
              <Col lg="6" className="mb-5 mb-lg-0">
                <div className="animate-on-scroll opacity-0 animate-fade-in-up">
                  <span className="pmr-group-badge">FOR RESIDENCY PROGRAMS</span>
                  <h2 className="pmr-section-title" style={{ marginTop: '1rem' }}>
                    Equip Your Entire Program &{' '}
                    <span className="pmr-text-gradient">Save Big</span>
                  </h2>
                  <p style={{ fontSize: '1.125rem', color: '#8898aa', lineHeight: 1.7, marginBottom: '2rem' }}>
                    Get exclusive group rates when you purchase 5+ seats.
                    Dedicated coordinator tools included.
                  </p>
                  <div className="pmr-group-benefits">
                    <div className="pmr-group-benefit">
                      <i className="fas fa-check-circle" />
                      <span>Significant discount on individual pricing</span>
                    </div>
                    <div className="pmr-group-benefit">
                      <i className="fas fa-check-circle" />
                      <span>Easy code distribution to residents</span>
                    </div>
                    <div className="pmr-group-benefit">
                      <i className="fas fa-check-circle" />
                      <span>Bulk access management</span>
                    </div>
                  </div>
                  <Link
                    to="/group-pricing"
                    className="btn pmr-btn-primary pmr-btn-lg"
                    style={{ marginTop: '2rem' }}
                  >
                    Get Group Pricing <i className="fas fa-arrow-right ml-2" />
                  </Link>
                </div>
              </Col>
              <Col lg="6">
                <div className="animate-on-scroll opacity-0 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                  <div className="pmr-group-card">
                    <div className="pmr-group-card-icon">
                      <i className="fas fa-hospital" />
                    </div>
                    <h3 className="pmr-group-card-title">Residency Program Plan</h3>
                    <p className="pmr-group-card-subtitle">Tailored for PM&R programs</p>
                    <div className="pmr-group-card-seats">
                      <i className="fas fa-users mr-2" />
                      5 &ndash; 50+ seats
                    </div>
                    <div className="pmr-group-card-highlight">
                      <i className="fas fa-tag mr-2" />
                      Exclusive Group Discount
                    </div>
                    <ul className="pmr-group-card-features">
                      <li><i className="fas fa-check mr-2" />Full Question Bank access</li>
                      <li><i className="fas fa-check mr-2" />PM&R Review Guide included</li>
                      <li><i className="fas fa-check mr-2" />Unique redemption codes</li>
                      <li><i className="fas fa-check mr-2" />Priority email support</li>
                    </ul>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Features Section */}
        <section id="features" className="pmr-features">
          <Container>
            <Row className="justify-content-center mb-5">
              <Col lg="8" className="text-center">
                <h2 className="pmr-section-title">
                  Everything You Need to <span className="pmr-text-gradient">Pass Your Boards</span>
                </h2>
                <p className="pmr-section-subtitle">
                  Our comprehensive platform provides all the tools you need for effective board preparation.
                </p>
              </Col>
            </Row>
            <Row>
              {features.map((feature, index) => (
                <Col key={index} md="6" lg="4" className="mb-4">
                  <div
                    id={`feature-${index}`}
                    className={`pmr-feature-card animate-on-scroll ${
                      isVisible[`feature-${index}`] ? 'animate-fade-in-up' : 'opacity-0'
                    }`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div
                      className="pmr-feature-icon"
                      style={{ backgroundColor: `${feature.color}20`, color: feature.color }}
                    >
                      <i className={feature.icon} />
                    </div>
                    <h3 className="pmr-feature-title">{feature.title}</h3>
                    <p className="pmr-feature-description">{feature.description}</p>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* Stats Section */}
        <section className="pmr-stats">
          <Container>
            <Row className="justify-content-center">
              {stats.map((stat, index) => (
                <Col key={index} xs="6" md="3" className="text-center mb-4 mb-md-0">
                  <div
                    id={`stat-${index}`}
                    className={`pmr-stat-item animate-on-scroll ${
                      isVisible[`stat-${index}`] ? 'animate-fade-in-up' : 'opacity-0'
                    }`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <p className="pmr-stat-number">{stat.number}</p>
                    <p className="pmr-stat-label">{stat.label}</p>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* Topics Section */}
        <section id="topics" className="pmr-topics">
          <Container>
            <Row className="justify-content-center mb-5">
              <Col lg="8" className="text-center">
                <h2 className="pmr-section-title text-white">
                  Comprehensive <span className="pmr-text-gradient-light">Topic Coverage</span>
                </h2>
                <p className="pmr-section-subtitle text-white-80">
                  Our question bank covers all major PM&R topics aligned with ABPMR content outline.
                </p>
              </Col>
            </Row>
            <Row className="justify-content-center">
              {topics.map((topic, index) => (
                <Col key={index} xs="6" md="4" lg="3" className="mb-3">
                  <div
                    id={`topic-${index}`}
                    className={`pmr-topic-tag animate-on-scroll ${
                      isVisible[`topic-${index}`] ? 'animate-fade-in-up' : 'opacity-0'
                    }`}
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <i className="fas fa-check-circle mr-2" />
                    {topic}
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* How It Works Section */}
        <section className="pmr-how-it-works">
          <Container>
            <Row className="justify-content-center mb-5">
              <Col lg="8" className="text-center">
                <h2 className="pmr-section-title">
                  How <span className="pmr-text-gradient">PM&R Prep</span> Works
                </h2>
                <p className="pmr-section-subtitle">
                  Get started in minutes and begin your journey to board certification.
                </p>
              </Col>
            </Row>
            <Row className="align-items-center">
              <Col md="4" className="text-center mb-4 mb-md-0">
                <div
                  id="step-1"
                  className={`pmr-step animate-on-scroll ${
                    isVisible['step-1'] ? 'animate-fade-in-up' : 'opacity-0'
                  }`}
                >
                  <div className="pmr-step-number">1</div>
                  <h4 className="pmr-step-title">Create Account</h4>
                  <p className="pmr-step-description">
                    Sign up for free and get instant access to sample questions.
                  </p>
                </div>
              </Col>
              <Col md="4" className="text-center mb-4 mb-md-0">
                <div
                  id="step-2"
                  className={`pmr-step animate-on-scroll ${
                    isVisible['step-2'] ? 'animate-fade-in-up' : 'opacity-0'
                  }`}
                  style={{ animationDelay: '0.2s' }}
                >
                  <div className="pmr-step-number">2</div>
                  <h4 className="pmr-step-title">Practice Questions</h4>
                  <p className="pmr-step-description">
                    Study with our board-style questions and detailed explanations.
                  </p>
                </div>
              </Col>
              <Col md="4" className="text-center">
                <div
                  id="step-3"
                  className={`pmr-step animate-on-scroll ${
                    isVisible['step-3'] ? 'animate-fade-in-up' : 'opacity-0'
                  }`}
                  style={{ animationDelay: '0.4s' }}
                >
                  <div className="pmr-step-number">3</div>
                  <h4 className="pmr-step-title">Pass Your Boards</h4>
                  <p className="pmr-step-description">
                    Track your progress and walk into your exam with confidence.
                  </p>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Fellowship Programs Section */}
        <section id="fellowship" className="pmr-fellowship-programs">
          <Container>
            <Row className="justify-content-center mb-5">
              <Col lg="8" className="text-center">
                <h2 className="pmr-section-title">
                  Preparing for a <span className="pmr-text-gradient">Fellowship?</span>
                </h2>
                <p className="pmr-section-subtitle">
                  We also offer dedicated question banks for PM&R fellowship subspecialties.
                </p>
              </Col>
            </Row>
            <Row className="justify-content-center">
              <Col md="5" className="mb-4">
                <div
                  id="fellowship-pain"
                  className={`pmr-fellowship-card pain animate-on-scroll ${
                    isVisible['fellowship-pain'] ? 'animate-fade-in-up' : 'opacity-0'
                  }`}
                >
                  <div className="pmr-fellowship-icon pain">
                    <i className="fas fa-head-side-virus" />
                  </div>
                  <h3>Pain Medicine</h3>
                  <p>
                    Comprehensive question bank for the Pain Medicine subspecialty board exam covering
                    interventional procedures, pharmacology, and more.
                  </p>
                  <Link
                    to="/fellowship/pain-medicine"
                    className="btn pmr-btn-fellowship pain"
                  >
                    Learn More <i className="fas fa-arrow-right ml-2" />
                  </Link>
                </div>
              </Col>
              <Col md="5" className="mb-4">
                <div
                  id="fellowship-sports"
                  className={`pmr-fellowship-card sports animate-on-scroll ${
                    isVisible['fellowship-sports'] ? 'animate-fade-in-up' : 'opacity-0'
                  }`}
                  style={{ animationDelay: '0.15s' }}
                >
                  <div className="pmr-fellowship-icon sports">
                    <i className="fas fa-running" />
                  </div>
                  <h3>Sports Medicine</h3>
                  <p>
                    Comprehensive question bank for the Sports Medicine subspecialty board exam covering
                    MSK injuries, concussion management, and more.
                  </p>
                  <Link
                    to="/fellowship/sports-medicine"
                    className="btn pmr-btn-fellowship sports"
                  >
                    Learn More <i className="fas fa-arrow-right ml-2" />
                  </Link>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Testimonials Section */}
        <section id="testimonials" className="pmr-testimonials">
          <Container>
            <Row className="justify-content-center mb-5">
              <Col lg="8" className="text-center">
                <h2 className="pmr-section-title">
                  What Our <span className="pmr-text-gradient">Users Say</span>
                </h2>
                <p className="pmr-section-subtitle">
                  Join hundreds of successful physicians who passed their boards with PM&R Prep.
                </p>
              </Col>
            </Row>
            <Row>
              {testimonials.map((testimonial, index) => (
                <Col key={index} md="4" className="mb-4">
                  <div
                    id={`testimonial-${index}`}
                    className={`pmr-testimonial-card animate-on-scroll ${
                      isVisible[`testimonial-${index}`] ? 'animate-fade-in-up' : 'opacity-0'
                    }`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="pmr-testimonial-stars">
                      {[...Array(5)].map((_, i) => (
                        <i key={i} className="fas fa-star" />
                      ))}
                    </div>
                    <p className="pmr-testimonial-text">"{testimonial.text}"</p>
                    <div className="pmr-testimonial-author">
                      <div className="pmr-testimonial-avatar">{testimonial.avatar}</div>
                      <div>
                        <h5 className="pmr-testimonial-name">{testimonial.name}</h5>
                        <p className="pmr-testimonial-role">{testimonial.role}</p>
                      </div>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="pmr-pricing">
          <Container>
            <Row className="justify-content-center mb-4">
              <Col lg="8" className="text-center">
                <h2 className="pmr-section-title text-white" style={{ marginBottom: '8px' }}>
                  Simple <span className="pmr-text-gradient-light">Pricing</span>
                </h2>
                <p className="pmr-section-subtitle text-white-80" style={{ marginBottom: '10px' }}>
                  Start with a 14-day free trial. You won't be charged until your trial ends.
                </p>
              </Col>
            </Row>

            {/* Top row: 1mo, 3mo, 6mo, 12mo */}
            <Row className="justify-content-center">
              {/* 1 Month */}
              <Col xs="6" md="3" className="mb-4">
                <div id="pricing-1" className={`pmr-pricing-card animate-on-scroll ${isVisible['pricing-1'] ? 'animate-fade-in-up' : 'opacity-0'}`}>
                  <div className="pmr-pricing-header">
                    <h3 className="pmr-pricing-name">1 Month</h3>
                    <div className="pmr-pricing-price">
                      <span className="pmr-price-amount">$99</span>
                    </div>
                  </div>
                  <Link className="btn pmr-btn-outline-dark btn-sm w-100 mt-3" to="/auth/register">
                    Get Started
                  </Link>
                </div>
              </Col>
              {/* 3 Months */}
              <Col xs="6" md="3" className="mb-4">
                <div id="pricing-2" className={`pmr-pricing-card animate-on-scroll ${isVisible['pricing-2'] ? 'animate-fade-in-up' : 'opacity-0'}`} style={{ animationDelay: '0.05s' }}>
                  <div className="pmr-pricing-header">
                    <h3 className="pmr-pricing-name">3 Months</h3>
                    <div className="pmr-pricing-price">
                      <span className="pmr-price-amount">$170</span>
                    </div>
                    <p style={{ color: '#8898aa', fontSize: '0.75rem', margin: '4px 0 0' }}>$56.67/mo</p>
                  </div>
                  <Link className="btn pmr-btn-outline-dark btn-sm w-100 mt-3" to="/auth/register">
                    Get Started
                  </Link>
                </div>
              </Col>
              {/* 6 Months */}
              <Col xs="6" md="3" className="mb-4">
                <div id="pricing-3" className={`pmr-pricing-card animate-on-scroll ${isVisible['pricing-3'] ? 'animate-fade-in-up' : 'opacity-0'}`} style={{ animationDelay: '0.1s' }}>
                  <div className="pmr-pricing-header">
                    <h3 className="pmr-pricing-name">6 Months</h3>
                    <div className="pmr-pricing-price">
                      <span className="pmr-price-amount">$219</span>
                    </div>
                    <p style={{ color: '#8898aa', fontSize: '0.75rem', margin: '4px 0 0' }}>$36.50/mo</p>
                  </div>
                  <Link className="btn pmr-btn-outline-dark btn-sm w-100 mt-3" to="/auth/register">
                    Get Started
                  </Link>
                </div>
              </Col>
              {/* 12 Months - Most Popular */}
              <Col xs="6" md="3" className="mb-4">
                <div id="pricing-4" className={`pmr-pricing-card pmr-pricing-featured animate-on-scroll ${isVisible['pricing-4'] ? 'animate-fade-in-up' : 'opacity-0'}`} style={{ animationDelay: '0.15s' }}>
                  <div className="pmr-pricing-badge">Most Popular</div>
                  <div className="pmr-pricing-header">
                    <h3 className="pmr-pricing-name">12 Months</h3>
                    <div className="pmr-pricing-price">
                      <span className="pmr-price-amount">$239</span>
                    </div>
                    <p style={{ color: '#8898aa', fontSize: '0.75rem', margin: '4px 0 0' }}>$19.92/mo</p>
                  </div>
                  <Link className="btn pmr-btn-primary btn-sm w-100 mt-3" to="/auth/register">
                    Get Started
                  </Link>
                </div>
              </Col>
            </Row>

            {/* Bottom row: 18mo, 24mo, 36mo, 48mo */}
            <Row className="justify-content-center">
              {/* 18 Months */}
              <Col xs="6" md="3" className="mb-4">
                <div id="pricing-5" className={`pmr-pricing-card animate-on-scroll ${isVisible['pricing-5'] ? 'animate-fade-in-up' : 'opacity-0'}`} style={{ animationDelay: '0.2s' }}>
                  <div className="pmr-pricing-header">
                    <h3 className="pmr-pricing-name">18 Months</h3>
                    <div className="pmr-pricing-price">
                      <span className="pmr-price-amount">$299</span>
                    </div>
                    <p style={{ color: '#8898aa', fontSize: '0.75rem', margin: '4px 0 0' }}>$16.61/mo</p>
                  </div>
                  <Link className="btn pmr-btn-outline-dark btn-sm w-100 mt-3" to="/auth/register">
                    Get Started
                  </Link>
                </div>
              </Col>
              {/* 24 Months */}
              <Col xs="6" md="3" className="mb-4">
                <div id="pricing-6" className={`pmr-pricing-card animate-on-scroll ${isVisible['pricing-6'] ? 'animate-fade-in-up' : 'opacity-0'}`} style={{ animationDelay: '0.25s' }}>
                  <div className="pmr-pricing-header">
                    <h3 className="pmr-pricing-name">24 Months</h3>
                    <div className="pmr-pricing-price">
                      <span className="pmr-price-amount">$329</span>
                    </div>
                    <p style={{ color: '#8898aa', fontSize: '0.75rem', margin: '4px 0 0' }}>$13.71/mo</p>
                  </div>
                  <Link className="btn pmr-btn-outline-dark btn-sm w-100 mt-3" to="/auth/register">
                    Get Started
                  </Link>
                </div>
              </Col>
              {/* 36 Months */}
              <Col xs="6" md="3" className="mb-4">
                <div id="pricing-7" className={`pmr-pricing-card animate-on-scroll ${isVisible['pricing-7'] ? 'animate-fade-in-up' : 'opacity-0'}`} style={{ animationDelay: '0.3s' }}>
                  <div className="pmr-pricing-header">
                    <h3 className="pmr-pricing-name">36 Months</h3>
                    <div className="pmr-pricing-price">
                      <span className="pmr-price-amount">$399</span>
                    </div>
                    <p style={{ color: '#8898aa', fontSize: '0.75rem', margin: '4px 0 0' }}>$11.08/mo</p>
                  </div>
                  <Link className="btn pmr-btn-outline-dark btn-sm w-100 mt-3" to="/auth/register">
                    Get Started
                  </Link>
                </div>
              </Col>
              {/* 48 Months - For incoming PGY1s */}
              <Col xs="6" md="3" className="mb-4">
                <div id="pricing-8" className={`pmr-pricing-card animate-on-scroll ${isVisible['pricing-8'] ? 'animate-fade-in-up' : 'opacity-0'}`} style={{ animationDelay: '0.35s' }}>
                  <div className="pmr-pricing-badge" style={{ backgroundColor: '#2dce89' }}>BEST For PGY1s</div>
                  <div className="pmr-pricing-header">
                    <h3 className="pmr-pricing-name">48 Months</h3>
                    <div className="pmr-pricing-price">
                      <span className="pmr-price-amount">$485</span>
                    </div>
                    <p style={{ color: '#8898aa', fontSize: '0.75rem', margin: '4px 0 0' }}>$10.10/mo</p>
                  </div>
                  <Link className="btn pmr-btn-outline-dark btn-sm w-100 mt-3" to="/auth/register">
                    Get Started
                  </Link>
                </div>
              </Col>
            </Row>

            {/* All Plans Include */}
            <Row className="justify-content-center mt-4">
              <Col lg="8" className="text-center">
                <div style={{
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  padding: '20px',
                  borderRadius: '10px'
                }}>
                  <h4 style={{ color: '#fff', marginBottom: '15px' }}>All Plans Include:</h4>
                  <Row>
                    <Col xs="6" md="4" className="mb-2">
                      <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.9rem' }}>
                        <i className="fas fa-check text-success mr-2"></i>Full Question Bank
                      </span>
                    </Col>
                    <Col xs="6" md="4" className="mb-2">
                      <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.9rem' }}>
                        <i className="fas fa-check text-success mr-2"></i>2,000+ Questions
                      </span>
                    </Col>
                    <Col xs="6" md="4" className="mb-2">
                      <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.9rem' }}>
                        <i className="fas fa-check text-success mr-2"></i>Detailed Explanations
                      </span>
                    </Col>
                    <Col xs="6" md="4" className="mb-2">
                      <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.9rem' }}>
                        <i className="fas fa-check text-success mr-2"></i>Performance Analytics
                      </span>
                    </Col>
                    <Col xs="6" md="4" className="mb-2">
                      <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.9rem' }}>
                        <i className="fas fa-check text-success mr-2"></i>Timed Practice Exams
                      </span>
                    </Col>
                    <Col xs="6" md="4" className="mb-2">
                      <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.9rem' }}>
                        <i className="fas fa-check text-success mr-2"></i>Mobile Access
                      </span>
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Bundle & Save Section */}
        <section id="bundles" className="pmr-bundles">
          <Container>
            <Row className="justify-content-center mb-5">
              <Col lg="8" className="text-center">
                <h2 className="pmr-section-title">
                  Bundle & <span className="pmr-text-gradient">Save</span>
                </h2>
                <p className="pmr-section-subtitle">
                  Studying for multiple specialties? Purchase them together and save up to 25%.
                </p>
              </Col>
            </Row>
            <Row className="justify-content-center">
              {BUNDLES.map((bundle, index) => {
                const basePlan = SUBSCRIPTION_PLANS.find(p => p.months === 12);
                const pricing = getBundlePrice(basePlan, bundle);
                return (
                  <Col key={bundle.id} md="6" lg="3" className="mb-4">
                    <div
                      id={`bundle-${index}`}
                      className={`pmr-bundle-card animate-on-scroll ${
                        isVisible[`bundle-${index}`] ? 'animate-fade-in-up' : 'opacity-0'
                      } ${bundle.popular ? 'pmr-bundle-featured' : ''}`}
                      style={{ animationDelay: `${index * 0.1}s` }}
                    >
                      {bundle.popular && (
                        <div className="pmr-pricing-badge" style={{ marginBottom: '10px' }}>Best Value</div>
                      )}
                      <div className="pmr-bundle-discount">
                        Save {bundle.discountPercent}%
                      </div>
                      <h3 className="pmr-bundle-name">{bundle.name}</h3>
                      <div className="pmr-bundle-specialties">
                        {bundle.specialties.map(specId => (
                          <span key={specId} className="pmr-bundle-specialty-tag">
                            <i className={`${getSpecialty(specId).icon} mr-1`} />
                            {getSpecialty(specId).name}
                          </span>
                        ))}
                      </div>
                      <div className="pmr-bundle-price">
                        <span className="pmr-bundle-price-amount">${pricing.totalPrice}</span>
                        <span className="pmr-bundle-price-original">${pricing.individualTotal}</span>
                      </div>
                      <p className="pmr-bundle-price-label">for 12 months</p>
                      <p className="pmr-bundle-savings">
                        You save ${pricing.savedAmount}
                      </p>
                      <Link className="btn pmr-btn-primary btn-sm w-100 mt-2" to="/auth/register">
                        Get Started
                      </Link>
                    </div>
                  </Col>
                );
              })}
            </Row>
            <Row className="justify-content-center mt-3">
              <Col lg="8" className="text-center">
                <p className="pmr-section-subtitle" style={{ fontSize: '0.95rem' }}>
                  <i className="fas fa-info-circle mr-2" style={{ color: 'var(--pmr-accent)' }} />
                  Bundle pricing is available for all plan durations. Choose your bundle during registration.
                </p>
              </Col>
            </Row>
          </Container>
        </section>

        {/* CTA Section */}
        <section className="pmr-cta">
          <Container>
            <Row className="justify-content-center">
              <Col lg="8" className="text-center">
                <h2 className="pmr-cta-title">
                  Ready to Pass Your PM&R Boards?
                </h2>
                <p className="pmr-cta-subtitle">
                  Start preparing for your PM&R boards with confidence.
                  Start your 14-day free trial today — you won't be charged until it ends.
                </p>
                <Link
                  className="btn pmr-btn-white pmr-btn-lg"
                  to="/auth/register"
                >
                  Start Free Trial
                  <i className="fas fa-arrow-right ml-2" />
                </Link>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Footer */}
        <footer className="pmr-footer">
          <Container>
            <Row>
              <Col lg="4" className="mb-4 mb-lg-0">
                <img
                  src={require('assets/img/brand/pmr-logo.png')}
                  alt="PM&R Prep"
                  className="pmr-footer-logo"
                />
                <p className="pmr-footer-description">
                  The most comprehensive PM&R board review question bank.
                  Built by PM&R physicians.
                </p>
                <div className="pmr-social-links">
                  <a href="mailto:support@pmrprep.io" aria-label="Email">
                    <i className="fas fa-envelope" />
                  </a>
                </div>
              </Col>
              <Col lg="2" md="4" className="mb-4 mb-md-0">
                <h5 className="pmr-footer-title">Product</h5>
                <ul className="pmr-footer-links">
                  <li><a href="#features">Features</a></li>
                  <li><a href="#pricing">Pricing</a></li>
                  <li><a href="#bundles">Bundles</a></li>
                  <li><a href="#fellowship">Fellowship</a></li>
                  <li><a href="#testimonials">Reviews</a></li>
                </ul>
              </Col>
              <Col lg="2" md="4" className="mb-4 mb-md-0">
                <h5 className="pmr-footer-title">Resources</h5>
                <ul className="pmr-footer-links">
                  <li><Link to="/free-anki-deck">Free Anki Deck</Link></li>
                  <li><Link to="/auth/register">Get Started</Link></li>
                  <li><Link to="/auth/login">Sign In</Link></li>
                  <li><a href="#faq">FAQ</a></li>
                </ul>
              </Col>
              <Col lg="2" md="4">
                <h5 className="pmr-footer-title">Contact</h5>
                <ul className="pmr-footer-links pmr-footer-contact">
                  <li>
                    <i className="fas fa-envelope mr-2" />
                    support@pmrprep.io
                  </li>
                </ul>
              </Col>
            </Row>
            <hr className="pmr-footer-divider" />
            <Row>
              <Col className="text-center">
                <p className="pmr-copyright">
                  &copy; {new Date().getFullYear()} PM&R Prep. All rights reserved.
                </p>
              </Col>
            </Row>
          </Container>
        </footer>
      </div>
    </>
  );
};

export default PMRPrepHome;
